import React, { useState, useEffect } from 'react';
import {
  faCheckCircle,
  faExclamationTriangle,
  faCalendarWeek,
  faUser,
  faUsers,
  faClock,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import { axiosRubyClient } from '@/util/axios/axiosClient';

import Card from '@/components/Card';
import Button from '@/components/Button';
import Form from '@/form';
import TextField from '@/form/TextField';
import TextAreaField from '@/form/TextAreaField';
import OptionField from '@/form/OptionField';
import SingleOptionGroupField from '@/form/SingleOptionGroupField';
import TextSelectField from '@/form/TextSelectField';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Heading, Subheading, Body } from '@/components/Typography';
import Text from '@/elements/Text';
import Alert from '@/components/Alert';
import Tabs from '@/components/Tabs';
import Icon from '@/elements/Icon';

interface JobCreationFormData {
  // Core parameters
  workers_count: number;
  existing_customer_id?: number;
  customer_id?: number;
  account_id?: number;
  skill_id?: number;
  job_type: string;
  job_category: string;
  start_date?: string;

  // Worker relationships
  favorites_count: number;
  blocked_count: number;
  lta_workers_count: number;
  regular_workers_count: number;

  // Job options
  quantity: number;
  same_worker_all_shifts: boolean;
  auto_accept_requests: boolean;
  require_background_checks: boolean;
  is_holiday: boolean;
  needs_approval: boolean;
  needs_review: boolean;
  share_contact_number: boolean;
  tax_type: string;
  job_hold_status: string;
  markup_percent: number;
  mandatory_break_time: number;

  // Shifts options
  shifts_count: number;
  job_duration_hours: number;
  create_multiple_shifts: boolean;
  shift_gap_hours: number;

  // Additional data
  with_timesheets: boolean;
  with_job_offers: boolean;
  with_job_requests: boolean;
  with_comments: boolean;
  with_tags: boolean;
  with_certificates: boolean;
  with_job_holders: boolean;
  create_worker_relationships: boolean;

  // Content options
  skill_name: string;
  instructions: string;
  address_instructions: string;
  contact_instructions: string;
  uniform_instructions: string;
}

interface WeeklyFormData {
  start_date: string;
  customer_name: string;
  customer_id?: number;
}

interface WeeklyJobData {
  day: string;
  date: string;
  jobs: Array<{
    type: string;
    status: string;
    workers: number;
    description: string;
  }>;
}

const JobCreator: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });
  const [weeklyData, setWeeklyData] = useState<WeeklyJobData[] | null>(null);
  const [skills, setSkills] = useState<
    Array<{
      id: number;
      name: string;
      category?: string;
      category_id?: number;
      description?: string;
      active?: boolean;
      requestable?: boolean;
      label?: string;
    }>
  >([]);

  // Fetch skills on component mount
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await axiosRubyClient.get('/api/job_creation/skills');
        const skillsData = response.data.data;

        console.log('Skills API response:', skillsData);
        // Ensure we always set an array
        if (Array.isArray(skillsData)) {
          setSkills(skillsData);
        } else if (skillsData && Array.isArray(skillsData.skills)) {
          // In case the response is wrapped in an object like { skills: [...] }
          setSkills(skillsData.skills);
        } else {
          console.warn('Skills API response is not an array:', skillsData);
          setSkills([]);
        }
      } catch (error) {
        console.error('Failed to fetch skills:', error);
        // Set empty array as fallback
        setSkills([]);
      }
    };

    fetchSkills();
  }, []);

  const handleIndividualSubmit = async (formData: JobCreationFormData) => {
    setIsLoading(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const response = await axiosRubyClient.post(
        '/api/job_creation',
        formData,
      );

      setSubmitStatus({
        type: 'success',
        message: 'Shift created successfully! Check the response in the console.',
      });

      console.log('Shift Creation Response:', response.data);
    } catch (error: any) {
      console.error('Shift Creation Error:', error);
      setSubmitStatus({
        type: 'error',
        message:
          error.response?.data?.error ||
          'Failed to create shift. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleWeeklySubmit = async (formData: WeeklyFormData) => {
    setIsLoading(true);
    setSubmitStatus({ type: null, message: '' });
    setWeeklyData(null);

    try {
      const response = await axiosRubyClient.post(
        '/api/job_creation/weekly_orchestration',
        formData,
      );

      // Transform the response into our display format
      const mockWeeklyData: WeeklyJobData[] = [
        {
          day: 'Monday',
          date: formData.start_date,
          jobs: [
            {
              type: 'Past Shift',
              status: 'Unapproved Timesheets',
              workers: 4,
              description: 'Past shift with unapproved timesheets',
            },
          ],
        },
        {
          day: 'Tuesday',
          date: getDateOffset(formData.start_date, 1),
          jobs: [
            {
              type: 'Ongoing Shift',
              status: 'Workers Checked In',
              workers: 4,
              description: 'Ongoing shift with checked-in workers',
            },
          ],
        },
        {
          day: 'Wednesday',
          date: getDateOffset(formData.start_date, 2),
          jobs: [
            {
              type: 'Future Shift',
              status: 'Fully Staffed',
              workers: 4,
              description: 'Future fully staffed shift',
            },
          ],
        },
        {
          day: 'Thursday',
          date: getDateOffset(formData.start_date, 3),
          jobs: [
            {
              type: 'Future Shift',
              status: 'Staffed',
              workers: 4,
              description: 'Future staffed shift',
            },
          ],
        },
        {
          day: 'Friday',
          date: getDateOffset(formData.start_date, 4),
          jobs: [
            {
              type: 'Future Shift',
              status: 'Fully Staffed',
              workers: 4,
              description: 'Fully staffed shift',
            },
            {
              type: 'Future Shift',
              status: 'Partial Staffing',
              workers: 1,
              description: 'Partial staffing (1/2 workers + invites)',
            },
          ],
        },
        {
          day: 'Saturday',
          date: getDateOffset(formData.start_date, 5),
          jobs: [
            {
              type: 'Future Shift',
              status: 'Fully Staffed',
              workers: 4,
              description: 'Fully staffed shift',
            },
            {
              type: 'Future Shift',
              status: 'Fully Staffed',
              workers: 2,
              description: 'Fully staffed shift',
            },
          ],
        },
        {
          day: 'Sunday',
          date: getDateOffset(formData.start_date, 6),
          jobs: [
            {
              type: 'Future Shift',
              status: 'Fully Staffed',
              workers: 4,
              description: 'Fully staffed shift',
            },
            {
              type: 'Future Shift',
              status: 'Fully Staffed',
              workers: 2,
              description: 'Fully staffed shift',
            },
          ],
        },
      ];

      setWeeklyData(mockWeeklyData);
      setSubmitStatus({
        type: 'success',
        message: 'Weekly orchestration created successfully!',
      });

      console.log('Weekly Orchestration Response:', response.data);
    } catch (error: any) {
      console.error('Weekly Orchestration Error:', error);
      setSubmitStatus({
        type: 'error',
        message:
          error.response?.data?.error ||
          'Failed to create weekly orchestration. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getDateOffset = (startDate: string, days: number): string => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'fully staffed':
        return '#2e7d32';
      case 'staffed':
        return '#388e3c';
      case 'workers checked in':
        return '#1976d2';
      case 'partial staffing':
        return '#f57f17';
      case 'unapproved timesheets':
        return '#d32f2f';
      default:
        return '#666';
    }
  };

  const getJobTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'past shift':
        return faClock;
      case 'ongoing shift':
        return faUsers;
      case 'future shift':
        return faCalendarWeek;
      default:
        return faUser;
    }
  };

  const jobTypeOptions = [
    { value: 'future', label: 'Future Shift' },
    { value: 'past_job', label: 'Past Shift' },
    { value: 'ongoing', label: 'Ongoing Shift' },
  ];

  const jobCategoryOptions = [
    { value: 'gig', label: 'Short-term' },
    { value: 'lta', label: 'Long-term' },
  ];

  const taxTypeOptions = [
    { value: 'w2', label: 'W2 Employee' },
    { value: '1099', label: '1099 Contractor' },
  ];

  const jobHoldStatusOptions = [
    { value: 'none', label: 'No Hold' },
    { value: 'hold', label: 'Full Hold' },
    { value: 'partial_hold', label: 'Partial Hold' },
  ];

  const tabs = [
    {
      id: 'individual',
      label: 'Individual Shift',
      icon: faUser,
      a11yLabel: 'Individual Shift Creation',
    },
    {
      id: 'weekly',
      label: 'Weekly Orchestration',
      icon: faCalendarWeek,
      a11yLabel: 'Weekly Shift Orchestration',
    },
  ];

  return (
    <Page title="Shift Creator - Create Dummy Records" size="xl">
      <Stack vertical gap={32}>
        <div>
          <Heading>Shift Creation Tool</Heading>
          <Body color="light">
            Create dummy shift records with customizable options for testing and
            development.
          </Body>
        </div>

        {submitStatus.type && (
          <Alert
            status={submitStatus.type === 'success' ? 'success' : 'danger'}
            icon={
              submitStatus.type === 'success'
                ? faCheckCircle
                : faExclamationTriangle
            }
            title={submitStatus.type === 'success' ? 'Success' : 'Error'}
            description={submitStatus.message}
          />
        )}

        <div style={{ marginBottom: '32px', width: '600px' }}>
          <Tabs tabs={tabs} selected={activeTab} onSelect={setActiveTab} />
        </div>

        {activeTab === 0 && (
          <Form<JobCreationFormData>
            initialValues={{
              // Core parameters defaults
              workers_count: 1,
              job_type: 'future',
              job_category: 'gig',
              skill_id: undefined,
              start_date: new Date().toISOString().split('T')[0],

              // Worker relationships defaults
              favorites_count: 0,
              blocked_count: 0,
              lta_workers_count: 0,
              regular_workers_count: 0,

              // Job options defaults
              quantity: 1,
              same_worker_all_shifts: false,
              auto_accept_requests: false,
              require_background_checks: false,
              is_holiday: false,
              needs_approval: false,
              needs_review: false,
              share_contact_number: true,
              tax_type: 'w2',
              job_hold_status: 'none',
              markup_percent: 60.0,
              mandatory_break_time: 0,

              // Shifts options defaults
              shifts_count: 1,
              job_duration_hours: 8.0,
              create_multiple_shifts: false,
              shift_gap_hours: 1.0,

              // Additional data defaults
              with_timesheets: false,
              with_job_offers: false,
              with_job_requests: false,
              with_comments: false,
              with_tags: false,
              with_certificates: false,
              with_job_holders: false,
              create_worker_relationships: false,

              // Content options defaults
              skill_name: '',
              instructions: '',
              address_instructions: '',
              contact_instructions: '',
              uniform_instructions: '',
            }}
            onSubmit={handleIndividualSubmit}
          >
            <Stack vertical gap={24}>
              {/* Basic Job Configuration */}
              <Card sectioned title="Basic Shift Configuration">
                <Stack vertical gap={24}>
                  {/* First Row - Core Fields */}
                  <Stack gap={24} wrap>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextField
                        fieldId="workers_count"
                        label="Number of Workers"
                        type="number"
                        min={1}
                        max={50}
                        placeholder="1"
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextSelectField
                        fieldId="job_type"
                        label="Shift Type"
                        options={jobTypeOptions}
                        placeholder="Select shift type"
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextSelectField
                        fieldId="job_category"
                        label="Shift Category"
                        options={jobCategoryOptions}
                        placeholder="Select shift category"
                      />
                    </div>
                  </Stack>

                  {/* Second Row - Date and Skill */}
                  <Stack gap={24} wrap>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextField
                        fieldId="start_date"
                        label="Start Date"
                        type="date"
                        required
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextSelectField
                        fieldId="skill_id"
                        label="Required Skill (Optional)"
                        options={(skills || []).map((skill) => ({
                          label: skill.label || skill.name,
                          value: skill.id.toString(),
                        }))}
                        placeholder="Select a skill..."
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      {/* Empty div for spacing */}
                    </div>
                  </Stack>

                  {/* Third Row - Optional IDs */}
                  <Stack gap={24} wrap>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextField
                        fieldId="customer_id"
                        label="Customer ID (Optional)"
                        type="number"
                        placeholder="Use existing customer ID"
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextField
                        fieldId="account_id"
                        label="Account ID (Optional)"
                        type="number"
                        placeholder="Use existing account/agency ID"
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      {/* Empty div for spacing */}
                    </div>
                  </Stack>
                </Stack>
              </Card>

              {/* Worker Relationships Section */}
              <Card sectioned title="Worker Relationships" accordion>
                <Stack gap={24} wrap>
                  <div style={{ flex: '1', minWidth: '300px' }}>
                    <TextField
                      fieldId="favorites_count"
                      label="Favorite Workers"
                      type="number"
                      min={0}
                      max={20}
                      placeholder="0"
                    />
                  </div>
                  <div style={{ flex: '1', minWidth: '300px' }}>
                    <TextField
                      fieldId="blocked_count"
                      label="Blocked Workers"
                      type="number"
                      min={0}
                      max={10}
                      placeholder="0"
                    />
                  </div>
                  <div style={{ flex: '1', minWidth: '300px' }}>
                    <TextField
                      fieldId="lta_workers_count"
                      label="Long-term Workers"
                      type="number"
                      min={0}
                      max={10}
                      placeholder="0"
                    />
                  </div>
                  <div style={{ flex: '1', minWidth: '300px' }}>
                    <TextField
                      fieldId="regular_workers_count"
                      label="Regular Workers"
                      type="number"
                      min={0}
                      max={50}
                      placeholder="0"
                    />
                  </div>
                </Stack>
              </Card>

              {/* Job Configuration Section */}
              <Card sectioned title="Shift Configuration" accordion>
                <Stack vertical gap={24}>
                  {/* Numeric Fields */}
                  <Stack gap={24} wrap>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextField
                        fieldId="quantity"
                        label="Worker Positions"
                        type="number"
                        min={1}
                        max={100}
                        placeholder="1"
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextField
                        fieldId="markup_percent"
                        label="Markup Percentage"
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        placeholder="60.0"
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextField
                        fieldId="mandatory_break_time"
                        label="Mandatory Break Time (minutes)"
                        type="number"
                        min={0}
                        max={120}
                        step={15}
                        placeholder="0"
                      />
                    </div>
                  </Stack>

                  {/* Select Fields */}
                  <Stack gap={24} wrap>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextSelectField
                        fieldId="tax_type"
                        label="Tax Classification"
                        options={taxTypeOptions}
                        placeholder="Select tax type"
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextSelectField
                        fieldId="job_hold_status"
                        label="Hold Status"
                        options={jobHoldStatusOptions}
                        placeholder="Select hold status"
                      />
                    </div>
                  </Stack>

                  {/* Job Options */}
                  <div>
                    <div style={{ marginBottom: '16px' }}>
                      <Text weight="medium">Shift Options</Text>
                    </div>
                    <Stack gap={24} wrap>
                      <div style={{ flex: '1', minWidth: '300px' }}>
                        <Stack vertical gap={12}>
                          <OptionField
                            fieldId="same_worker_all_shifts"
                            label="Same Workers All Shifts"
                          />
                          <OptionField
                            fieldId="auto_accept_requests"
                            label="Auto Accept Requests"
                          />
                          <OptionField
                            fieldId="require_background_checks"
                            label="Require Background Checks"
                          />
                        </Stack>
                      </div>
                      <div style={{ flex: '1', minWidth: '300px' }}>
                        <Stack vertical gap={12}>
                          <OptionField
                            fieldId="is_holiday"
                            label="Holiday Shift"
                          />
                          <OptionField
                            fieldId="needs_approval"
                            label="Needs Approval"
                          />
                          <OptionField
                            fieldId="needs_review"
                            label="Needs Review"
                          />
                        </Stack>
                      </div>
                      <div style={{ flex: '1', minWidth: '300px' }}>
                        <OptionField
                          fieldId="share_contact_number"
                          label="Share Contact Number"
                        />
                      </div>
                    </Stack>
                  </div>
                </Stack>
              </Card>

              {/* Shift Configuration Section */}
              <Card sectioned title="Shift Configuration" accordion>
                <Stack vertical gap={24}>
                  <Stack gap={24} wrap>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextField
                        fieldId="shifts_count"
                        label="Number of Shifts"
                        type="number"
                        min={1}
                        max={30}
                        placeholder="1"
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextField
                        fieldId="job_duration_hours"
                        label="Shift Duration (hours)"
                        type="number"
                        min={0.5}
                        max={24.0}
                        step={0.5}
                        placeholder="8.0"
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextField
                        fieldId="shift_gap_hours"
                        label="Gap Between Shifts (hours)"
                        type="number"
                        min={0}
                        max={168}
                        step={0.5}
                        placeholder="1.0"
                      />
                    </div>
                  </Stack>

                  <OptionField
                    fieldId="create_multiple_shifts"
                    label="Create Multiple Shifts"
                  />
                </Stack>
              </Card>

              {/* Additional Features Section */}
              <Card sectioned title="Additional Features" accordion>
                <div style={{ marginBottom: '20px' }}>
                  <Text weight="medium">Extra Data & Features</Text>
                </div>
                <Stack gap={24} wrap>
                  <div style={{ flex: '1', minWidth: '300px' }}>
                    <Stack vertical gap={12}>
                      <OptionField
                        fieldId="with_timesheets"
                        label="Include Timesheets (for past shifts)"
                      />
                      <OptionField
                        fieldId="with_job_offers"
                        label="Create Shift Offers"
                      />
                      <OptionField
                        fieldId="with_job_requests"
                        label="Create Shift Requests"
                      />
                    </Stack>
                  </div>
                  <div style={{ flex: '1', minWidth: '300px' }}>
                    <Stack vertical gap={12}>
                      <OptionField
                        fieldId="with_comments"
                        label="Add Comments"
                      />
                      <OptionField fieldId="with_tags" label="Add Tags" />
                      <OptionField
                        fieldId="with_certificates"
                        label="Include Certificates"
                      />
                    </Stack>
                  </div>
                  <div style={{ flex: '1', minWidth: '300px' }}>
                    <Stack vertical gap={12}>
                      <OptionField
                        fieldId="with_job_holders"
                        label="Create Shift Holders"
                      />
                      <OptionField
                        fieldId="create_worker_relationships"
                        label="Create Worker Relationships"
                      />
                    </Stack>
                  </div>
                </Stack>
              </Card>

              {/* Job Content & Instructions Section */}
              <Card sectioned title="Shift Content & Instructions" accordion>
                <Stack vertical gap={24}>
                  <TextAreaField
                    fieldId="instructions"
                    label="General Instructions"
                    maxLength={1000}
                    rows={4}
                    placeholder="Please arrive 15 minutes early and check in with the manager..."
                  />

                  <Stack gap={24} wrap>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextAreaField
                        fieldId="address_instructions"
                        label="Location Instructions"
                        maxLength={500}
                        rows={3}
                        placeholder="Use the staff entrance on the south side of the building..."
                      />
                    </div>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                      <TextAreaField
                        fieldId="contact_instructions"
                        label="Contact Instructions"
                        maxLength={500}
                        rows={3}
                        placeholder="Call the manager at extension 123 upon arrival..."
                      />
                    </div>
                  </Stack>

                  <TextAreaField
                    fieldId="uniform_instructions"
                    label="Uniform & Dress Code"
                    maxLength={500}
                    rows={2}
                    placeholder="Black pants, white shirt, black shoes. Hair must be tied back..."
                  />
                </Stack>
              </Card>

              {/* Submit Button */}
              <Card sectioned>
                <Stack justify="end">
                  <Button
                    a11yLabel="Create Shift"
                    label="Create Shift"
                    appearance="primary"
                    type="submit"
                    isLoading={isLoading}
                    loadingLabel="Creating Shift..."
                  />
                </Stack>
              </Card>
            </Stack>
          </Form>
        )}

        {activeTab === 1 && (
          <Stack vertical gap={32}>
            <Form<WeeklyFormData>
              initialValues={{
                start_date: new Date().toISOString().split('T')[0],
                customer_name: '',
                customer_id: undefined,
              }}
              onSubmit={handleWeeklySubmit}
            >
              <Stack vertical gap={32}>
                <Card sectioned title="Weekly Orchestration Configuration">
                  <Stack vertical gap={24}>
                    <div style={{ maxWidth: '400px' }}>
                      <TextField
                        fieldId="start_date"
                        label="Start Date (Monday)"
                        type="date"
                        required
                      />
                    </div>

                    <div style={{ maxWidth: '400px' }}>
                      <TextField
                        fieldId="customer_name"
                        label="Customer Name (Optional)"
                        placeholder="Enter customer name (optional)..."
                      />
                    </div>

                    <div style={{ maxWidth: '400px' }}>
                      <TextField
                        fieldId="customer_id"
                        label="Customer ID (Optional)"
                        type="number"
                        min={1}
                        placeholder="Enter customer ID (optional)..."
                      />
                    </div>

                    <div
                      style={{
                        padding: '24px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid #e9ecef',
                      }}
                    >
                      <div style={{ marginBottom: '16px' }}>
                        <Heading>
                          <Icon
                            icon={faCalendarWeek}
                            style={{ marginRight: '8px', color: '#495057' }}
                          />
                          Weekly Shift Schedule Preview
                        </Heading>
                      </div>
                      <div style={{ marginBottom: '20px' }}>
                        <Body color="light">
                          This will create a comprehensive week of test jobs
                          starting from the selected Monday:
                        </Body>
                      </div>

                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fit, minmax(300px, 1fr))',
                          gap: '16px',
                        }}
                      >
                        <div
                          style={{
                            padding: '16px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                          }}
                        >
                          <Stack vertical gap={8}>
                            <div style={{ color: '#d32f2f' }}>
                              <Text weight="medium">
                                <Icon
                                  icon={faClock}
                                  style={{ marginRight: '6px' }}
                                />
                                Monday
                              </Text>
                            </div>
                            <Text size="sm">
                              Past shift with unapproved timesheets
                            </Text>
                            <Text size="sm" color="light">
                              4 workers
                            </Text>
                          </Stack>
                        </div>

                        <div
                          style={{
                            padding: '16px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                          }}
                        >
                          <Stack vertical gap={8}>
                            <div style={{ color: '#1976d2' }}>
                              <Text weight="medium">
                                <Icon
                                  icon={faUsers}
                                  style={{ marginRight: '6px' }}
                                />
                                Tuesday
                              </Text>
                            </div>
                            <Text size="sm">
                              Ongoing shift with checked-in workers
                            </Text>
                            <Text size="sm" color="light">
                              4 workers
                            </Text>
                          </Stack>
                        </div>

                        <div
                          style={{
                            padding: '16px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                          }}
                        >
                          <Stack vertical gap={8}>
                            <div style={{ color: '#2e7d32' }}>
                              <Text weight="medium">
                                <Icon
                                  icon={faCalendarWeek}
                                  style={{ marginRight: '6px' }}
                                />
                                Wednesday
                              </Text>
                            </div>
                            <Text size="sm">Future fully staffed shift</Text>
                            <Text size="sm" color="light">
                              4 workers
                            </Text>
                          </Stack>
                        </div>

                        <div
                          style={{
                            padding: '16px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                          }}
                        >
                          <Stack vertical gap={8}>
                            <div style={{ color: '#ff9800' }}>
                              <Text weight="medium">
                                <Icon
                                  icon={faCalendarWeek}
                                  style={{ marginRight: '6px' }}
                                />
                                Thursday
                              </Text>
                            </div>
                            <Text size="sm">
                              Future shift - no workers hired yet
                            </Text>
                            <Text size="sm" color="light">
                              0/4 workers
                            </Text>
                          </Stack>
                        </div>

                        <div
                          style={{
                            padding: '16px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                          }}
                        >
                          <Stack vertical gap={8}>
                            <div style={{ color: '#f57f17' }}>
                              <Text weight="medium">
                                <Icon
                                  icon={faMapMarkerAlt}
                                  style={{ marginRight: '6px' }}
                                />
                                Friday
                              </Text>
                            </div>
                            <Text size="sm">
                              Shift needs 4 workers but only 1 hired
                            </Text>
                            <Text size="sm" color="light">
                              1/4 workers hired
                            </Text>
                          </Stack>
                        </div>

                        <div
                          style={{
                            padding: '16px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6',
                          }}
                        >
                          <Stack vertical gap={8}>
                            <div style={{ color: '#2e7d32' }}>
                              <Text weight="medium">
                                <Icon
                                  icon={faUsers}
                                  style={{ marginRight: '6px' }}
                                />
                                Weekend
                              </Text>
                            </div>
                            <Text size="sm">
                              Saturday & Sunday: 2 fully staffed shifts each
                            </Text>
                            <Text size="sm" color="light">
                              4 + 2 workers per day
                            </Text>
                          </Stack>
                        </div>
                      </div>
                    </div>

                    <Stack justify="end">
                      <Button
                        a11yLabel="Create Weekly Orchestration"
                        label="Create Weekly Orchestration"
                        appearance="primary"
                        type="submit"
                        isLoading={isLoading}
                        loadingLabel="Creating Weekly Shifts..."
                      />
                    </Stack>
                  </Stack>
                </Card>
              </Stack>
            </Form>

            {weeklyData && (
              <Card sectioned title="Weekly Orchestration Results">
                <Stack vertical gap={16}>
                  {weeklyData.map((dayData, index) => (
                    <Card key={index} sectioned>
                      <Stack vertical gap={12}>
                        <Stack justify="apart" align="center">
                          <Subheading>{dayData.day}</Subheading>
                          <Text size="sm" color="light">
                            {dayData.date}
                          </Text>
                        </Stack>

                        <Stack vertical gap={8}>
                          {dayData.jobs.map((job, jobIndex) => (
                            <div
                              key={jobIndex}
                              style={{
                                padding: '12px 16px',
                                backgroundColor: '#fafafa',
                                borderRadius: '6px',
                                border: `2px solid ${getStatusColor(job.status)}20`,
                              }}
                            >
                              <Stack justify="apart" align="center">
                                <Stack gap={12} align="center">
                                  <Icon
                                    icon={getJobTypeIcon(job.type)}
                                    style={{
                                      color: getStatusColor(job.status),
                                    }}
                                  />
                                  <Stack vertical gap={4} align="start">
                                    <Text weight="medium">{job.type}</Text>
                                    <Text size="sm" color="light">
                                      {job.description}
                                    </Text>
                                  </Stack>
                                </Stack>

                                <Stack gap={16} align="center">
                                  <div
                                    style={{
                                      padding: '4px 8px',
                                      backgroundColor: getStatusColor(
                                        job.status,
                                      ),
                                      color: 'white',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {job.status}
                                  </div>
                                  <Stack gap={4} align="center">
                                    <Icon icon={faUsers} />
                                    <Text weight="medium">{job.workers}</Text>
                                  </Stack>
                                </Stack>
                              </Stack>
                            </div>
                          ))}
                        </Stack>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </Card>
            )}
          </Stack>
        )}
      </Stack>
    </Page>
  );
};

export default JobCreator;
